import { StatsInfo } from "src/components/compounds/GameStats/stat-info-type";
import { GridItem, PlayerWithLinkColumn, PlayerWithoutLinkColumn } from "../common-columns";
import { eventDateFormatter, inningsFormatter } from "../formatters";

// Cell highlight colors for a fielder's positions; the StatsLegend swatches use the same hexes.
export const PRIMARY_POSITION_BG = "#3d4f63";
export const SECONDARY_POSITION_BG = "#d7e3f0";

// Innings-by-position fields (the Total column "IP:F" is deliberately excluded). A row's position
// with the most innings is the player's primary position (dark); any other position with innings is
// secondary (light) — mirroring GameChanger's fielding view.
const POSITION_FIELDS = ["ip", "IC:C", "IP:1B", "IP:2B", "IP:3B", "IP:SS", "IP:LF", "IP:CF", "IP:RF", "IP:SF"];

const positionCellStyle = (params: any): Record<string, unknown> => {
  const base = { textAlign: "center" as const };
  // Don't highlight the pinned "Team" total row — only individual players have a primary position.
  if (params.node?.rowPinned) return base;
  const value = Number(params.value) || 0;
  if (value <= 0) return base;
  const max = Math.max(...POSITION_FIELDS.map((f) => Number(params.data?.[f]) || 0));
  if (max <= 0) return base;
  return value === max
    ? { ...base, backgroundColor: PRIMARY_POSITION_BG, color: "#fff", fontWeight: 600 }
    : { ...base, backgroundColor: SECONDARY_POSITION_BG };
};

export interface FieldingInningsPlayedFormattedRow {
  player: {
    id: string;
    teamID: string;
    name: string;
    status: string;
  };
  ["ip"]: number;
  ["IC:C"]: number;
  ["IP:1B"]: number;
  ["IP:2B"]: number;
  ["IP:3B"]: number;
  ["IP:CF"]: number;
  ["IP:F"]: number;
  ["IP:LF"]: number;
  ["IP:RF"]: number;
  ["IP:SF"]: number;
  ["IP:SS"]: number;
}

export const inningsPlayedGridDataFormatted = (grid: GridItem[]): FieldingInningsPlayedFormattedRow[] => {
  return grid
    .filter(({ defense }) => defense && defense["IP:F"] !== 0)
    .map(({ person_id, team_id, name, eventID, date, defense }) => {
      return {
        player: {
          id: person_id,
          teamID: team_id,
          name,
          eventID,
          status: "active",
        },
        // Read by the per-game Date column; undefined (and ignored) in season/box-score views.
        event: date,
        "ip": defense.ip ?? 0,
        "IC:C": defense["IC:C"],
        "IP:1B": defense["IP:1B"],
        "IP:2B": defense["IP:2B"],
        "IP:3B": defense["IP:3B"],
        "IP:CF": defense["IP:CF"],
        "IP:F": defense["IP:F"],
        "IP:LF": defense["IP:LF"],
        "IP:RF": defense["IP:RF"],
        "IP:SF": defense["IP:SF"],
        "IP:SS": defense["IP:SS"],
      };
    });
};

export const CoreFieldingInningsPlayed = (includeSort: boolean): StatsInfo[] => [
  {
    name: "IP",
    field: "ip",
    def: "Innings Pitched",
    valueFormatter: inningsFormatter,
    cellStyle: positionCellStyle,
    columnProps: {
      ...(includeSort ? { sort: "desc" } : {}),
    },
  },
  {
    name: "C",
    field: "IC:C",
    def: "Innings Catching",
    valueFormatter: inningsFormatter,
    cellStyle: positionCellStyle,
  },
  {
    name: "1B",
    field: "IP:1B",
    def: "Innings at First Base",
    valueFormatter: inningsFormatter,
    cellStyle: positionCellStyle,
  },
  {
    name: "2B",
    field: "IP:2B",
    def: "Innings at Second Base",
    valueFormatter: inningsFormatter,
    cellStyle: positionCellStyle,
  },
  {
    name: "3B",
    field: "IP:3B",
    def: "Innings at Third Base",
    valueFormatter: inningsFormatter,
    cellStyle: positionCellStyle,
  },
  {
    name: "SS",
    field: "IP:SS",
    def: "Innings at Short Stop",
    valueFormatter: inningsFormatter,
    cellStyle: positionCellStyle,
  },
  {
    name: "LF",
    field: "IP:LF",
    def: "Innings in Left Field",
    valueFormatter: inningsFormatter,
    cellStyle: positionCellStyle,
  },
  {
    name: "CF",
    field: "IP:CF",
    def: "Innings in Center Field",
    valueFormatter: inningsFormatter,
    cellStyle: positionCellStyle,
  },
  {
    name: "RF",
    field: "IP:RF",
    def: "Innings in Right Field",
    valueFormatter: inningsFormatter,
    cellStyle: positionCellStyle,
  },
  {
    name: "SF",
    field: "IP:SF",
    def: "Innings at Short Field",
    valueFormatter: inningsFormatter,
    cellStyle: positionCellStyle,
  },
  {
    name: "Total",
    field: "IP:F",
    def: "Total Innings Fielding",
    valueFormatter: inningsFormatter,
  },
];

export const FieldingInningsPlayed: StatsInfo[] = [PlayerWithLinkColumn, ...CoreFieldingInningsPlayed(true)];

export const EventFieldingInningsPlayed: StatsInfo[] = [
  {
    name: "Date",
    field: "event",
    valueFormatter: eventDateFormatter,
    sort: "asc",
    columnProps: {
      width: 100,
      pinned: true,
      cellStyle: { textAlign: "left" },
    },
  },
  {
    name: "OPPONENT",
    field: "opponent",
    columnProps: {
      width: 260,
      cellStyle: { textAlign: "left" },
      headerClass: "left-aligned-column-header",
    },
  } as StatsInfo,
  ...CoreFieldingInningsPlayed(false),
];

export const GameFieldingInningsPlayed: StatsInfo[] = [PlayerWithoutLinkColumn, ...CoreFieldingInningsPlayed(true)];
